import React, { useCallback, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useAuthContext } from '../../../../context/auth.context'
import { useApiListAuthOrganization } from '../../../../api-new/state/auth'
import { useLoad } from '../../../../shared/hooks/load'
import { decodeJwtClaims } from '../../../../shared/jwt'
import { getAccessToken } from '../../../../storage/auth'
import { AuthHead, AuthLoading, AuthNotice, AuthPage } from '../../../../shared/sso-workspace/auth-card'
import { OrganizationChooser, mostRecentKey, orgKey } from '../../../../shared/sso-workspace/organization-chooser'
import { needsOrganizationChoice, useLoginRedirect } from '../hooks/redirect'
import { invitationsText, usePendingInvites } from '../../../../shared/sso-workspace/pending-invites'
import { goBackOr } from '../../../../shared/hooks/go-back'
import { restorable } from '../../../../shared/organization-deletion/organization-deletion'
import { ScheduledSheet } from '../../../../shared/organization-deletion/scheduled-sheet'

// /auth/account: which organization to work in. Two ways here:
//
//   * right after a password or device sign-in that has to ask -- the person
//     belongs to several company organizations and has not chosen one, so
//     the token came back with multipleAccounts and no lastOrganizationID
//     (useLoginRedirect sends them here). "Last used" flags the organization
//     signed in to most recently;
//   * "Change Organization" in the account menu. "Current" flags the
//     organization this session is in.
//
// Either way the pick goes through the organization switch (PUT
// /auth-organization/:refreshToken), which the auth service records as the
// person's choice, then on to wherever the sign-in was going.
//
// Switching (not after a sign-in that has to ask) has a Cancel that goes back
// to where the person came from without switching, and the current
// organization is not a switch target: choosing it goes back too. Pending
// invitations (from any organization) show as a notice linking to
// /auth/invitations. Many organizations: the shared chooser searches and
// scrolls, and shows the ones opened most recently under "Recent" (owner
// 2026-10-07).
//
// An organization scheduled for deletion is listed to every member until it
// is purged (scheduled-org member experience, D5). Only a row the server
// marks restorable switches (into the owner's restore session); any other
// opens the information sheet -- what happened, until when, whom to contact
// -- and switches nothing.
export function LoginAccount() {
  const history = useHistory()
  const pending = usePendingInvites()

  const authContext = useAuthContext()

  const apiListAuthOrganization = useApiListAuthOrganization()

  const loginRedirect = useLoginRedirect()

  const [claims] = useState(() => decodeJwtClaims(getAccessToken()) || {})
  const mustChoose = needsOrganizationChoice(claims)

  // Switching: the organization this session is in.
  const currentKey = !mustChoose && claims.organizationID != null ? String(claims.organizationID) : null

  // loading -> choose | selecting -> (leaves) ; failed on a load error
  const [state, updateState] = useState({ step: 'loading', organizations: [], chosen: null, error: false, flagKey: null })

  useLoad(async () => {
    try {
      // The server's order (by name, scheduled after live): the chooser puts
      // the flagged or current row first, or shows Recent above it.
      const organizations = (await apiListAuthOrganization.get(authContext.state.refreshToken)) || []
      // Must choose: "Last used" is the organization this sign-in was put in
      // (the server's most recently used company organization); else the
      // most recently signed-in one. Switching: Current (currentKey).
      const signedInto = claims.organizationID != null ? String(claims.organizationID) : null
      const flagKey = mustChoose ? (organizations.some(o => orgKey(o) === signedInto) ? signedInto : mostRecentKey(organizations)) : null
      updateState({ step: 'choose', organizations, chosen: null, error: false, flagKey })
    } catch {
      updateState(prev => ({ ...prev, step: 'failed' }))
    }
  }, [])

  const [sheet, updateSheet] = useState(null)
  const closeSheet = useCallback(() => updateSheet(null), [])

  const handleSelect = async organization => {
    if (organization.scheduledForDeletion && !restorable(organization)) {
      updateSheet(organization)
      return
    }

    // Already here: nothing to switch, back to where the person came from.
    if (!organization.scheduledForDeletion && currentKey != null && orgKey(organization) === currentKey) {
      goBackOr(history)
      return
    }

    updateState(prev => ({ ...prev, step: 'selecting', chosen: organization, error: false }))

    try {
      // chosen: the person picked it -- the auth service remembers it as
      // their choice (a personal organization included).
      const authToken = await apiListAuthOrganization.put(authContext.state.refreshToken, { ...organization, chosen: true })
      authContext.setAuthToken(authToken)
      loginRedirect({ skipChoice: true })
    } catch {
      updateState(prev => ({ ...prev, step: 'choose', error: true }))
    }
  }

  if (state.step === 'failed') {
    return (
      <AuthPage labelledBy="account-title">
        <AuthHead id="account-title" title="We couldn't load your organizations" />
        <AuthNotice tone="bad" role="alert">
          <p>Try again in a moment. If this keeps happening, sign out and sign in again.</p>
        </AuthNotice>
        <Link replace className="btn sw-btn-primary sa-block" to="/auth/email">Back to sign in</Link>
      </AuthPage>
    )
  }

  return (
    <AuthPage labelledBy="account-title">
      <AuthHead
        id="account-title"
        title={mustChoose ? 'Choose an organization' : 'Switch organization'}
        lede={mustChoose
          ? 'Your account belongs to more than one EducationXR organization. Choose the one to use now. You can switch organizations at any time after signing in.'
          : 'Choose the organization to work in.'}
      />

      {pending.count > 0 &&
        <AuthNotice>
          <p>
            {invitationsText(pending.count)} <Link to="/auth/invitations">Review invitations</Link>
          </p>
        </AuthNotice>}

      {state.step === 'loading' && <AuthLoading>Finding your organizations…</AuthLoading>}

      {state.step === 'selecting' && state.chosen &&
        <AuthLoading>Switching to {state.chosen.name}…</AuthLoading>}

      {state.error &&
        <AuthNotice tone="bad" role="alert">
          <p>We couldn't switch to {(state.chosen && state.chosen.name) || 'that organization'}. Try again, or choose another.</p>
        </AuthNotice>}

      {state.step === 'choose' &&
        <OrganizationChooser
          organizations={state.organizations}
          flagKey={state.flagKey}
          flagLabel="Last used"
          currentKey={currentKey}
          recent
          labelledBy="account-title"
          onSelect={handleSelect}
        />}

      <ScheduledSheet organization={sheet} open={!!sheet} onClose={closeSheet} />

      {!mustChoose &&
        <div className="sa-chooser-foot">
          <button type="button" className="btn sw-btn" disabled={state.step === 'selecting'} onClick={() => goBackOr(history)}>
            Cancel
          </button>
        </div>}
    </AuthPage>
  )
}
